import type { LocalFile } from '@frontend/api-types';
import { objectToStrJson } from '@frontend/duck-tape';
import { useEffect, useRef, useState, useTemporaryAnimation } from '@frontend/react';
import { PageContainer, YStack } from '@frontend/web-react';
import { applyClass } from '@frontend/web-utils';
import { ChatFooter } from '@frontend/web/components';
import { ThreadLander } from '@frontend/web/components/DomainSpecific/Threads/ThreadLander';
import { useAnalytics, useCreateThreadMutation } from '@frontend/web/hooks';
import { handleMutation } from '@frontend/web/utils';
import { createFileRoute } from '@tanstack/react-router';

const CreateThread = () => {
  const navigate = Route.useNavigate();
  const inputRef = useRef<HTMLAreaElement>();
  const { animate, isAnimating } = useTemporaryAnimation({ duration: 1000 });
  const [createThread, { isLoading: isCreateThreadLoading }] = useCreateThreadMutation();
  const [messageValue, setMessageValue] = useState('');
  const [files, setFiles] = useState<LocalFile[]>([]);
  const [currentlyUsedExample, setCurrentlyUsedExample] = useState<string>();
  const { track } = useAnalytics();

  // If input gets cleared, input can't be based off of an suggestion
  useEffect(() => {
    if (!messageValue) {
      setCurrentlyUsedExample(undefined);
    }
  }, [messageValue]);

  const onClickSubmit = async () => {
    if (currentlyUsedExample) {
      track({
        data: {
          example: currentlyUsedExample,
          threadDescription: messageValue,
        },
        event: 'researchThreadFromExampleCreated',
        topic: 'research',
      });
    }

    handleMutation({
      mutation: async () => {
        const result = await createThread().unwrap();
        return result;
      },
      onSuccess: ({ id: threadId }) => {
        setMessageValue('');
        inputRef.current?.blur();
        return navigate({
          params: { threadId },
          search: { autoMessageSendContent: messageValue, files: objectToStrJson(files) },
          to: '/app/thread/$threadId',
        });
      },
      showSuccessToast: false,
    });
  };

  return (
    <PageContainer className="p-md max-w-screen-md self-center" isScrollable={false} padding="none">
      <YStack className="grow-1 items-center justify-center">
        <ThreadLander
          setInputMessage={(messageValue) => {
            animate();
            setMessageValue(messageValue);
          }}
        />
      </YStack>
      <ChatFooter
        className={applyClass(isAnimating, 'slam-effect')}
        filesValue={files}
        isSubmitLoading={isCreateThreadLoading}
        onChangeFiles={(files) => setFiles(files)}
        onChangeText={(messageValue) => setMessageValue(messageValue)}
        onClickSubmit={onClickSubmit}
        placeholder="Enter message"
        submitShortcut="Enter"
        textInputDisabled={isCreateThreadLoading}
        textValue={messageValue}
      />
    </PageContainer>
  );
};

export const Route = createFileRoute('/app/thread/create')({
  component: CreateThread,
});
